// @ts-nocheck
/**
 * ツインキーシステム - 魔導錬金画面
 * @module twinKey/ui/screens/AlchemyScreen
 */

import React, { useState, useMemo, useCallback, useRef } from 'react';
import { economyAccount, economyRequest } from '../../../lib/economyClient';
import { useTwinKeyContext } from '../../context/TwinKeyContext';
import { useTwinKey, useDrops } from '../../hooks';
import { alchemyEngine } from '../../content/alchemy';
import {
  getMaterialInfo,
  EXP_ORB_MATERIALS,
  calculateOrbUseCost,
  getAllExpOrbs,
} from '../../content/alchemy/data/materialTypes';
import { getRequiredExp } from '../../data/constants';
import { RECIPES, getRecipe, getRecipesByType } from '../../content/alchemy/data/recipes';
import { getEffectDescription } from '../../content/alchemy/data/effectDescriptions';
import styles from '../styles/twinKeyStyles';
import { useIsMobile } from '../../../collection/hooks/useMediaQuery';

// レアリティの色定義
const RARITY_COLORS = {
  C: '#888888',
  UC: '#4a9c4a',
  R: '#3498db',
  SR: '#9b59b6',
  SSR: '#f1c40f',
  UR: '#e67e22',
  LR: '#e74c3c',
  MR: '#ff69b4',
  EX: '#8e44ad',
  GR: '#00d4ff',
};

// ============================================================
// 錬金画面スタイル（錬金術テーマ）
// ============================================================

const alchemyColors = {
  gold: '#ffd700',
  copper: '#b87333',
  purple: '#8b5cf6',
  green: '#10b981',
  blue: '#3b82f6',
  red: '#ef4444',
  darkPurple: '#1a0a2e',
  glowPurple: 'rgba(139, 92, 246, 0.3)',
};

const alchemyStyles = {
  container: {
    ...styles.container,
    background: `linear-gradient(180deg, #0a0a1a 0%, ${alchemyColors.darkPurple} 100%)`,
  },

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: `1px solid ${alchemyColors.purple}30`,
    background: 'rgba(139, 92, 246, 0.05)',
  },

  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    background: `linear-gradient(135deg, ${alchemyColors.gold}, ${alchemyColors.purple})`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },

  backButton: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: 'rgba(139, 92, 246, 0.2)',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '14px',
  },

  tabBar: {
    display: 'flex',
    gap: '0',
    borderBottom: `2px solid ${alchemyColors.purple}30`,
  },

  tab: {
    flex: 1,
    padding: '16px 24px',
    border: 'none',
    background: 'transparent',
    color: '#a0a0b0',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    borderBottom: '3px solid transparent',
    transition: 'all 0.2s ease',
  },

  tabActive: {
    color: alchemyColors.gold,
    borderBottom: `3px solid ${alchemyColors.gold}`,
    background: `linear-gradient(180deg, rgba(255, 215, 0, 0.1) 0%, transparent 100%)`,
  },

  // モバイル用コンテナ
  containerMobile: {
    ...styles.container,
    background: `linear-gradient(180deg, #0a0a1a 0%, ${alchemyColors.darkPurple} 100%)`,
    paddingTop: '56px',
    paddingBottom: '70px',
  },

  // モバイル用固定ヘッダー
  headerMobile: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: `1px solid ${alchemyColors.purple}30`,
    background: 'rgba(10, 10, 26, 0.98)',
    zIndex: 100,
  },

  // フッタータブナビゲーション（モバイル用）
  footerTabNavigation: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'space-around',
    padding: '8px 4px',
    paddingBottom: 'calc(8px + env(safe-area-inset-bottom))',
    background: 'linear-gradient(180deg, rgba(26, 10, 46, 0.98) 0%, rgba(10, 10, 26, 0.99) 100%)',
    borderTop: `2px solid ${alchemyColors.purple}`,
    boxShadow: `0 -4px 20px ${alchemyColors.glowPurple}`,
    zIndex: 100,
  },

  footerTab: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px 2px',
    border: 'none',
    background: 'transparent',
    color: '#808090',
    fontSize: '10px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    borderRadius: '8px',
    margin: '0 2px',
  },

  footerTabActive: {
    color: alchemyColors.gold,
    background: `linear-gradient(180deg, rgba(255, 215, 0, 0.2) 0%, rgba(139, 92, 246, 0.1) 100%)`,
  },

  footerTabIcon: {
    fontSize: '20px',
    marginBottom: '2px',
  },

  main: {
    flex: 1,
    padding: '20px',
    overflowY: 'auto',
  },

  section: {
    marginBottom: '24px',
  },

  sectionTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: alchemyColors.gold,
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },

  materialsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
    gap: '12px',
  },

  materialCard: {
    padding: '12px',
    borderRadius: '8px',
    background: 'rgba(139, 92, 246, 0.1)',
    border: '1px solid rgba(139, 92, 246, 0.3)',
    textAlign: 'center',
    transition: 'all 0.2s ease',
  },

  materialIcon: {
    fontSize: '24px',
    marginBottom: '4px',
  },

  materialName: {
    fontSize: '11px',
    color: '#fff',
    marginBottom: '2px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  materialCount: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: alchemyColors.gold,
  },

  recipesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '16px',
  },

  recipeCard: {
    padding: '16px',
    borderRadius: '10px',
    background: 'rgba(26, 26, 46, 0.8)',
    border: '1px solid rgba(139, 92, 246, 0.3)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },

  recipeCardHover: {
    borderColor: alchemyColors.gold,
    boxShadow: `0 0 20px ${alchemyColors.glowPurple}`,
  },

  recipeCardCraftable: {
    borderColor: alchemyColors.green,
    background: 'rgba(16, 185, 129, 0.1)',
  },

  recipeCardLocked: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },

  recipeName: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#fff',
    marginBottom: '8px',
  },

  recipeTier: {
    fontSize: '11px',
    padding: '2px 8px',
    borderRadius: '4px',
    display: 'inline-block',
    marginBottom: '8px',
  },

  recipeStats: {
    fontSize: '11px',
    color: '#a0a0b0',
    marginBottom: '8px',
  },

  recipeMaterials: {
    fontSize: '10px',
    color: '#606070',
  },

  equipmentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '16px',
  },

  equipmentCard: {
    padding: '16px',
    borderRadius: '10px',
    background: 'rgba(26, 26, 46, 0.8)',
    border: '1px solid rgba(139, 92, 246, 0.3)',
    position: 'relative',
  },

  equipmentName: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#fff',
    marginBottom: '4px',
  },

  equipmentLevel: {
    fontSize: '12px',
    color: alchemyColors.gold,
    marginBottom: '8px',
  },

  equipmentStats: {
    fontSize: '11px',
    color: '#a0a0b0',
    marginBottom: '8px',
  },

  enhanceButton: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    fontSize: '12px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },

  enhanceButtonLevel: {
    background: `linear-gradient(135deg, ${alchemyColors.blue}, ${alchemyColors.purple})`,
    color: '#fff',
    marginRight: '8px',
  },

  enhanceButtonAwaken: {
    background: `linear-gradient(135deg, ${alchemyColors.gold}, ${alchemyColors.copper})`,
    color: '#000',
  },

  enhanceButtonDisabled: {
    background: '#333',
    color: '#666',
    cursor: 'not-allowed',
  },

  emptyMessage: {
    textAlign: 'center',
    color: '#606070',
    padding: '40px',
    fontSize: '14px',
  },

  // ボタンは他ページと同じサイズ
  primaryButton: {
    ...styles.primaryButton,
    background: `linear-gradient(135deg, ${alchemyColors.purple}, ${alchemyColors.gold})`,
  },

  secondaryButton: {
    ...styles.secondaryButton,
    borderColor: alchemyColors.purple,
  },

  // フィルター
  filterBar: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },

  filterButton: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: '1px solid rgba(139, 92, 246, 0.3)',
    background: 'transparent',
    color: '#a0a0b0',
    fontSize: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },

  filterButtonActive: {
    background: alchemyColors.purple,
    color: '#fff',
    borderColor: alchemyColors.purple,
  },

  // モーダル
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },

  modalContent: {
    background: '#1a1a2e',
    borderRadius: '12px',
    padding: '24px',
    maxWidth: '500px',
    width: '90%',
    maxHeight: '80vh',
    overflow: 'auto',
    border: `2px solid ${alchemyColors.purple}`,
  },

  modalTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: alchemyColors.gold,
    marginBottom: '16px',
    textAlign: 'center',
  },

  modalActions: {
    display: 'flex',
    gap: '12px',
    marginTop: '20px',
    justifyContent: 'center',
  },

  // 素材要件表示
  materialRequirement: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
    borderBottom: '1px solid rgba(139, 92, 246, 0.2)',
    fontSize: '13px',
  },

  materialSufficient: {
    color: alchemyColors.green,
  },

  materialInsufficient: {
    color: alchemyColors.red,
  },

  // 分解タブ用スタイル
  decomposeLayout: {
    display: 'grid',
    gridTemplateColumns: '1fr 300px',
    gap: '20px',
  },

  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
    gap: '12px',
    maxHeight: '400px',
    overflowY: 'auto',
    padding: '4px',
  },

  cardItem: {
    padding: '8px',
    borderRadius: '8px',
    background: 'rgba(26, 26, 46, 0.8)',
    border: '2px solid transparent',
    cursor: 'pointer',
    textAlign: 'center',
    transition: 'all 0.2s ease',
  },

  cardItemSelected: {
    borderColor: alchemyColors.gold,
    boxShadow: `0 0 12px ${alchemyColors.glowPurple}`,
  },

  cardName: {
    fontSize: '11px',
    color: '#fff',
    marginBottom: '4px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  cardRarity: {
    fontSize: '10px',
    fontWeight: 'bold',
    padding: '2px 6px',
    borderRadius: '4px',
    display: 'inline-block',
  },

  cardCount: {
    fontSize: '10px',
    color: '#a0a0b0',
    marginTop: '4px',
  },

  previewPanel: {
    padding: '16px',
    borderRadius: '10px',
    background: 'rgba(26, 26, 46, 0.9)',
    border: `1px solid ${alchemyColors.purple}50`,
  },

  previewTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: alchemyColors.gold,
    marginBottom: '12px',
    textAlign: 'center',
  },

  previewCardName: {
    fontSize: '14px',
    color: '#fff',
    marginBottom: '8px',
    textAlign: 'center',
  },

  previewMaterialsList: {
    marginTop: '16px',
  },

  previewMaterialItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px',
    borderBottom: '1px solid rgba(139, 92, 246, 0.2)',
    fontSize: '12px',
  },

  decomposeButton: {
    width: '100%',
    padding: '12px',
    marginTop: '16px',
    borderRadius: '8px',
    border: 'none',
    background: `linear-gradient(135deg, ${alchemyColors.red}, ${alchemyColors.purple})`,
    color: '#fff',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },

  decomposeButtonDisabled: {
    background: '#333',
    color: '#666',
    cursor: 'not-allowed',
  },

  ownedMaterialsSection: {
    marginTop: '24px',
    padding: '16px',
    borderRadius: '10px',
    background: 'rgba(26, 26, 46, 0.6)',
    border: `1px solid ${alchemyColors.purple}30`,
  },

  filterSection: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
    flexWrap: 'wrap',
    alignItems: 'center',
  },

  searchInput: {
    padding: '8px 12px',
    borderRadius: '6px',
    border: `1px solid ${alchemyColors.purple}50`,
    background: 'rgba(26, 26, 46, 0.8)',
    color: '#fff',
    fontSize: '12px',
    flex: '1',
    minWidth: '150px',
  },
};

// ティア別の色
const TIER_COLORS = {
  common: '#888',
  rare: '#3498db',
  epic: '#9b59b6',
  legendary: '#ffd700',
};

/**
 * 魔導錬金画面
 */
export function AlchemyScreen() {
  const { state, playerData, allCards, updatePlayerData } = useTwinKeyContext();
  const { goToHub } = useTwinKey();
  const { materials: playerMaterials } = useDrops();
  const isMobile = useIsMobile();

  // タブ状態（分解・作成・強化・育成の4タブ）
  const [activeTab, setActiveTab] = useState('decompose'); // 'decompose', 'craft', 'enhance', 'training'
  // 分解タブ用状態
  const [selectedCardForDecompose, setSelectedCardForDecompose] = useState(null);
  const [decomposeSearchQuery, setDecomposeSearchQuery] = useState('');
  const [decomposeRarityFilter, setDecomposeRarityFilter] = useState('all');

  // 選択中のレシピ
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  // レシピフィルター
  const [recipeFilter, setRecipeFilter] = useState('all'); // 'all', 'weapon', 'armor', 'accessory', 'special'
  // 選択中の装備（強化用）
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  // 分解結果ダイアログ
  const [decomposeResult, setDecomposeResult] = useState(null);
  // 作成結果ダイアログ
  const [craftResult, setCraftResult] = useState(null);

  // コレクションデータ（playerDataから取得）
  const collection = useMemo(() => {
    return playerData?.collection || [];
  }, [playerData]);

  // 分解可能なカード（コレクションをグループ化）
  const decomposableCards = useMemo(() => {
    const grouped = {};
    for (const item of collection) {
      const key = `${item.cardId}_${item.rarity}`;
      if (!grouped[key]) {
        // allCardsからカード情報を取得
        const cardInfo = allCards?.find(c => c.id === item.cardId) || { name: item.cardId };
        grouped[key] = {
          cardId: item.cardId,
          rarity: item.rarity,
          count: 0,
          card: cardInfo,
        };
      }
      grouped[key].count += item.quantity || 0;
    }

    // フィルタリング
    let result = Object.values(grouped);

    if (decomposeSearchQuery) {
      const query = decomposeSearchQuery.toLowerCase();
      result = result.filter(item =>
        item.card.name?.toLowerCase().includes(query) ||
        item.cardId.toLowerCase().includes(query)
      );
    }

    if (decomposeRarityFilter !== 'all') {
      result = result.filter(item => item.rarity === decomposeRarityFilter);
    }

    // レアリティ順でソート（高レアリティが上）
    const rarityOrder = { GR: 0, EX: 1, MR: 2, LR: 3, UR: 4, SSR: 5, SR: 6, R: 7, UC: 8, C: 9 };
    result.sort((a, b) => (rarityOrder[a.rarity] || 10) - (rarityOrder[b.rarity] || 10));

    return result;
  }, [collection, allCards, decomposeSearchQuery, decomposeRarityFilter]);

  // 分解プレビュー
  const decomposePreview = useMemo(() => {
    if (!selectedCardForDecompose) return null;
    return alchemyEngine.previewDecomposition(
      selectedCardForDecompose.card,
      selectedCardForDecompose.rarity
    );
  }, [selectedCardForDecompose]);

  // エンジンから素材情報を取得
  const allMaterials = useMemo(() => {
    const materials = [];
    const storedMaterials = playerData?.twinKey?.materials || {};

    for (const [type, count] of Object.entries(storedMaterials)) {
      if (count > 0) {
        const info = getMaterialInfo(type);
        materials.push({
          type,
          count,
          info: info || { name: type, tier: 'common', icon: '📦' },
        });
      }
    }

    // ティア順でソート
    const tierOrder = { legendary: 0, epic: 1, rare: 2, uncommon: 3, common: 4 };
    materials.sort((a, b) => {
      const tierDiff = (tierOrder[a.info.tier] || 5) - (tierOrder[b.info.tier] || 5);
      if (tierDiff !== 0) return tierDiff;
      return b.count - a.count;
    });

    return materials;
  }, [playerData]);

  // 作成した装備
  const equipment = useMemo(() => {
    return playerData?.twinKey?.equipment || [];
  }, [playerData]);

  // フィルタリングされたレシピ
  const filteredRecipes = useMemo(() => {
    if (recipeFilter === 'all') {
      return Object.values(RECIPES);
    }
    return getRecipesByType(recipeFilter);
  }, [recipeFilter]);

  // レシピが作成可能かチェック
  const canCraftRecipe = useCallback((recipe) => {
    const storedMaterials = playerData?.twinKey?.materials || {};
    for (const mat of recipe.materials) {
      const have = storedMaterials[mat.type] || 0;
      if (have < mat.count) return false;
    }
    return true;
  }, [playerData]);

  // A receipt is applied to local materials once, even after a lost response.
  const decomposing = useRef(false);
  const handleDecompose = useCallback(async () => {
    if (!selectedCardForDecompose || !updatePlayerData || decomposing.current) return;
    decomposing.current = true;
    const { card, rarity, cardId } = selectedCardForDecompose;
    try {
      const online = !!economyAccount();
      const result = alchemyEngine.decomposeCard(card, rarity);
      if (!result.success) throw new Error(result.message);
      const receipt = online ? await economyRequest('collection/decompose', { card_id: cardId, rarity }) : null;
      updatePlayerData(prev => {
        const twinKey = { ...prev.twinKey };
        const receipts = twinKey.decompositionReceipts || [];
        if (receipt && receipts.includes(receipt.decomposition_id)) return prev;
        let collection = prev.collection;
        if (!online) {
          const owned = collection.find(item => item.cardId === cardId && item.rarity === rarity);
          if (!owned || owned.quantity < 1) return prev;
          collection = collection.map(item => item === owned ? { ...item, quantity: item.quantity - 1 } : item)
            .filter(item => item.quantity > 0);
        }
        const materials = { ...twinKey.materials };
        for (const mat of result.materials) materials[mat.type] = (materials[mat.type] || 0) + mat.count;
        return { ...prev, collection, twinKey: { ...twinKey, materials,
          decompositionReceipts: receipt ? [...receipts, receipt.decomposition_id].slice(-200) : receipts } };
      });
      setSelectedCardForDecompose(null);
      setDecomposeResult({ success: true, cardName: card.name, rarity, materials: result.materials });
    } catch (error) {
      setDecomposeResult({ success: false, message: error.message });
    } finally { decomposing.current = false; }
  }, [selectedCardForDecompose, updatePlayerData]);

  // レシピ作成
  const handleCraft = useCallback(() => {
    if (!selectedRecipe || !updatePlayerData) return;

    // 素材をplayerDataから確認
    const storedMaterials = playerData?.twinKey?.materials || {};
    const missingMaterials = [];
    for (const mat of selectedRecipe.materials) {
      const have = storedMaterials[mat.type] || 0;
      if (have < mat.count) {
        const matInfo = getMaterialInfo(mat.type);
        missingMaterials.push({
          type: mat.type,
          name: matInfo?.name || mat.type,
          have,
          need: mat.count,
        });
      }
    }
    if (missingMaterials.length > 0) {
      setCraftResult({
        success: false,
        message: '素材が足りません',
        missingMaterials,
      });
      return;
    }

    // 素材消費と装備作成
    updatePlayerData((prev) => {
      const twinKey = { ...(prev.twinKey || {}) };
      const materials = { ...(twinKey.materials || {}) };
      const equipment = [...(twinKey.equipment || [])];

      if (selectedRecipe.materials.some(mat => (materials[mat.type] || 0) < mat.count)) return prev;
      // 素材消費
      for (const mat of selectedRecipe.materials) {
        materials[mat.type] = (materials[mat.type] || 0) - mat.count;
        if (materials[mat.type] <= 0) {
          delete materials[mat.type];
        }
      }

      // 装備作成
      const newEquipment = {
        id: `eq_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        recipeId: selectedRecipe.id,
        name: selectedRecipe.name,
        type: selectedRecipe.type,
        tier: selectedRecipe.tier,
        level: 1,
        awakening: 0,
        stats: { ...selectedRecipe.baseStats },
        effects: selectedRecipe.effects || [],
        createdAt: Date.now(),
      };
      equipment.push(newEquipment);

      twinKey.materials = materials;
      twinKey.equipment = equipment;

      return { ...prev, twinKey };
    });

    // 結果ダイアログを表示
    setCraftResult({
      success: true,
      equipmentName: selectedRecipe.name,
      tier: selectedRecipe.tier,
      stats: selectedRecipe.baseStats,
    });
    setSelectedRecipe(null);
  }, [selectedRecipe, playerData, updatePlayerData]);

  // 強化（TODO: 強化結果ダイアログの実装）
  const handleEnhance = useCallback((eq, type) => {
    const result = alchemyEngine.enhanceEquipment(eq, type);
    // 現在は素材消費なしのため簡易表示
    // 将来的にはダイアログに置き換え
    console.log(result.message);
  }, []);

  return (
    <div style={isMobile ? alchemyStyles.containerMobile : alchemyStyles.container}>
      {/* ヘッダー */}
      <div style={isMobile ? alchemyStyles.headerMobile : alchemyStyles.header}>
        <button style={alchemyStyles.backButton} onClick={goToHub}>
          ← 戻る
        </button>
        <h1 style={{ ...alchemyStyles.title, fontSize: isMobile ? '18px' : '24px' }}>⚗️ 魔導錬金所</h1>
        <div style={{ width: isMobile ? '50px' : '60px' }} />
      </div>

      {/* タブバー（PC用） */}
      {!isMobile && (
        <div style={alchemyStyles.tabBar}>
          <button
            style={{
              ...alchemyStyles.tab,
              ...(activeTab === 'decompose' ? alchemyStyles.tabActive : {}),
            }}
            onClick={() => setActiveTab('decompose')}
          >
            🔥 分解
          </button>
          <button
            style={{
              ...alchemyStyles.tab,
              ...(activeTab === 'craft' ? alchemyStyles.tabActive : {}),
            }}
            onClick={() => setActiveTab('craft')}
          >
            🔨 作成
          </button>
          <button
            style={{
              ...alchemyStyles.tab,
              ...(activeTab === 'enhance' ? alchemyStyles.tabActive : {}),
            }}
            onClick={() => setActiveTab('enhance')}
          >
            ✨ 強化
          </button>
          <button
            style={{
              ...alchemyStyles.tab,
              ...(activeTab === 'training' ? alchemyStyles.tabActive : {}),
            }}
            onClick={() => setActiveTab('training')}
          >
            📈 育成
          </button>
        </div>
      )}

      {/* メインコンテンツ */}
      <div style={alchemyStyles.main}>
        {/* 分解タブ */}
        {activeTab === 'decompose' && (
          <DecomposeTab
            decomposableCards={decomposableCards}
            selectedCard={selectedCardForDecompose}
            onSelectCard={setSelectedCardForDecompose}
            onDecompose={handleDecompose}
            preview={decomposePreview}
            searchQuery={decomposeSearchQuery}
            onSearchChange={setDecomposeSearchQuery}
            rarityFilter={decomposeRarityFilter}
            onRarityFilterChange={setDecomposeRarityFilter}
            materials={allMaterials}
          />
        )}

        {/* 作成タブ */}
        {activeTab === 'craft' && (
          <CraftTab
            recipes={filteredRecipes}
            filter={recipeFilter}
            setFilter={setRecipeFilter}
            canCraft={canCraftRecipe}
            onSelectRecipe={setSelectedRecipe}
            playerMaterials={playerData?.twinKey?.materials || {}}
          />
        )}

        {/* 強化タブ */}
        {activeTab === 'enhance' && (
          <EnhanceTab
            equipment={equipment}
            onEnhance={handleEnhance}
            playerMaterials={playerData?.twinKey?.materials || {}}
          />
        )}

        {/* 育成タブ */}
        {activeTab === 'training' && (
          <TrainingTab
            playerData={playerData}
            updatePlayerData={updatePlayerData}
            allCards={allCards}
          />
        )}
      </div>

      {/* レシピ詳細モーダル */}
      {selectedRecipe && (
        <RecipeModal
          recipe={selectedRecipe}
          canCraft={canCraftRecipe(selectedRecipe)}
          playerMaterials={playerData?.twinKey?.materials || {}}
          onCraft={handleCraft}
          onClose={() => setSelectedRecipe(null)}
        />
      )}

      {/* 分解結果ダイアログ */}
      {decomposeResult && (
        <ResultModal
          type="decompose"
          result={decomposeResult}
          onClose={() => setDecomposeResult(null)}
        />
      )}

      {/* 作成結果ダイアログ */}
      {craftResult && (
        <ResultModal
          type="craft"
          result={craftResult}
          onClose={() => setCraftResult(null)}
        />
      )}

      {/* フッタータブナビゲーション（モバイル用） */}
      {isMobile && (
        <div style={alchemyStyles.footerTabNavigation}>
          <button
            style={{
              ...alchemyStyles.footerTab,
              ...(activeTab === 'decompose' ? alchemyStyles.footerTabActive : {}),
            }}
            onClick={() => setActiveTab('decompose')}
          >
            <span style={alchemyStyles.footerTabIcon}>🔥</span>
            分解
          </button>
          <button
            style={{
              ...alchemyStyles.footerTab,
              ...(activeTab === 'craft' ? alchemyStyles.footerTabActive : {}),
            }}
            onClick={() => setActiveTab('craft')}
          >
            <span style={alchemyStyles.footerTabIcon}>🔨</span>
            作成
          </button>
          <button
            style={{
              ...alchemyStyles.footerTab,
              ...(activeTab === 'enhance' ? alchemyStyles.footerTabActive : {}),
            }}
            onClick={() => setActiveTab('enhance')}
          >
            <span style={alchemyStyles.footerTabIcon}>✨</span>
            強化
          </button>
          <button
            style={{
              ...alchemyStyles.footerTab,
              ...(activeTab === 'training' ? alchemyStyles.footerTabActive : {}),
            }}
            onClick={() => setActiveTab('training')}
          >
            <span style={alchemyStyles.footerTabIcon}>📈</span>
            育成
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * 分解タブ
 */
function DecomposeTab({
  decomposableCards,
  selectedCard,
  onSelectCard,
  onDecompose,
  preview,
  searchQuery,
  onSearchChange,
  rarityFilter,
  onRarityFilterChange,
  materials,
}) {
  const rarities = ['all', 'GR', 'EX', 'MR', 'LR', 'UR', 'SSR', 'SR', 'R', 'UC', 'C'];

  return (
    <div>
      {/* フィルターセクション */}
      <div style={alchemyStyles.filterSection}>
        <input
          type="text"
          placeholder="カード名で検索..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          style={alchemyStyles.searchInput}
        />
        <select
          value={rarityFilter}
          onChange={(e) => onRarityFilterChange(e.target.value)}
          style={{
            ...alchemyStyles.filterButton,
            background: 'rgba(26, 26, 46, 0.8)',
            minWidth: '80px',
          }}
        >
          <option value="all">全レア</option>
          {rarities.slice(1).map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      </div>

      <div style={alchemyStyles.decomposeLayout}>
        {/* カード一覧 */}
        <div>
          <h2 style={alchemyStyles.sectionTitle}>
            🃏 所持カード ({decomposableCards.length}種類)
          </h2>
          {decomposableCards.length === 0 ? (
            <div style={alchemyStyles.emptyMessage}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🃏</div>
              <div>分解可能なカードがありません</div>
            </div>
          ) : (
            <div style={alchemyStyles.cardGrid}>
              {decomposableCards.map((item) => {
                const isSelected = selectedCard?.cardId === item.cardId &&
                                   selectedCard?.rarity === item.rarity;
                return (
                  <div
                    key={`${item.cardId}_${item.rarity}`}
                    style={{
                      ...alchemyStyles.cardItem,
                      ...(isSelected ? alchemyStyles.cardItemSelected : {}),
                      borderColor: isSelected ? alchemyColors.gold : (RARITY_COLORS[item.rarity] || '#666'),
                    }}
                    onClick={() => onSelectCard(isSelected ? null : item)}
                  >
                    <div style={alchemyStyles.cardName} title={item.card.name}>
                      {item.card.name || item.cardId}
                    </div>
                    <div
                      style={{
                        ...alchemyStyles.cardRarity,
                        background: RARITY_COLORS[item.rarity] || '#666',
                        color: ['SSR', 'GR'].includes(item.rarity) ? '#000' : '#fff',
                      }}
                    >
                      {item.rarity}
                    </div>
                    <div style={alchemyStyles.cardCount}>×{item.count}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* プレビューパネル */}
        <div style={alchemyStyles.previewPanel}>
          <h3 style={alchemyStyles.previewTitle}>
            {selectedCard ? '分解プレビュー' : 'カードを選択'}
          </h3>

          {selectedCard ? (
            <>
              <div style={alchemyStyles.previewCardName}>
                {selectedCard.card.name || selectedCard.cardId}
                <span
                  style={{
                    marginLeft: '8px',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    background: RARITY_COLORS[selectedCard.rarity] || '#666',
                    color: ['SSR', 'GR'].includes(selectedCard.rarity) ? '#000' : '#fff',
                    fontSize: '12px',
                  }}
                >
                  {selectedCard.rarity}
                </span>
              </div>

              {preview && (
                <div style={alchemyStyles.previewMaterialsList}>
                  <div style={{ color: '#a0a0b0', fontSize: '12px', marginBottom: '8px' }}>
                    獲得素材:
                  </div>
                  {preview.materials.map((mat, idx) => {
                    const info = getMaterialInfo(mat.type);
                    return (
                      <div key={idx} style={alchemyStyles.previewMaterialItem}>
                        <span>
                          {info?.icon || '📦'} {info?.name || mat.type}
                        </span>
                        <span style={{ color: alchemyColors.green, fontWeight: 'bold' }}>
                          +{mat.count}
                        </span>
                      </div>
                    );
                  })}
                  <div style={{
                    marginTop: '12px',
                    padding: '8px',
                    background: 'rgba(139, 92, 246, 0.1)',
                    borderRadius: '6px',
                    textAlign: 'center',
                    fontSize: '12px',
                    color: '#a0a0b0',
                  }}>
                    推定価値: <span style={{ color: alchemyColors.gold }}>{preview.totalValue}G</span>
                  </div>
                </div>
              )}

              <button
                style={{
                  ...alchemyStyles.decomposeButton,
                  ...(selectedCard.count <= 0 ? alchemyStyles.decomposeButtonDisabled : {}),
                }}
                onClick={onDecompose}
                disabled={selectedCard.count <= 0}
              >
                🔥 分解する（残り {selectedCard.count}枚）
              </button>
            </>
          ) : (
            <div style={{ textAlign: 'center', color: '#606070', padding: '40px 0' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>👈</div>
              <div>左からカードを選択してください</div>
            </div>
          )}
        </div>
      </div>

      {/* 所持素材一覧（参考） */}
      <div style={alchemyStyles.ownedMaterialsSection}>
        <h3 style={{ ...alchemyStyles.sectionTitle, fontSize: '14px' }}>
          📦 所持素材 ({materials.length}種類)
        </h3>
        {materials.length === 0 ? (
          <div style={{ color: '#606070', fontSize: '12px', textAlign: 'center', padding: '20px' }}>
            素材がありません
          </div>
        ) : (
          <div style={{ ...alchemyStyles.materialsGrid, maxHeight: '150px', overflowY: 'auto' }}>
            {materials.slice(0, 20).map((mat) => (
              <div
                key={mat.type}
                style={{
                  ...alchemyStyles.materialCard,
                  borderColor: TIER_COLORS[mat.info.tier] || '#666',
                  padding: '8px',
                }}
              >
                <div style={{ fontSize: '16px' }}>{mat.info.icon || '📦'}</div>
                <div style={{ fontSize: '10px', color: '#fff' }}>{mat.info.name || mat.type}</div>
                <div style={{ fontSize: '12px', color: alchemyColors.gold }}>x{mat.count}</div>
              </div>
            ))}
            {materials.length > 20 && (
              <div style={{
                padding: '8px',
                textAlign: 'center',
                color: '#606070',
                fontSize: '11px',
              }}>
                他 {materials.length - 20}種類...
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * 作成タブ
 */
function CraftTab({ recipes, filter, setFilter, canCraft, onSelectRecipe, playerMaterials }) {
  const filters = [
    { key: 'all', label: '全て' },
    { key: 'weapon', label: '武器' },
    { key: 'armor', label: '防具' },
    { key: 'accessory', label: 'アクセ' },
    { key: 'special', label: '特殊' },
  ];

  return (
    <div>
      {/* フィルター */}
      <div style={alchemyStyles.filterBar}>
        {filters.map((f) => (
          <button
            key={f.key}
            style={{
              ...alchemyStyles.filterButton,
              ...(filter === f.key ? alchemyStyles.filterButtonActive : {}),
            }}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* レシピ一覧 */}
      <div style={alchemyStyles.recipesGrid}>
        {recipes.map((recipe) => {
          const craftable = canCraft(recipe);
          return (
            <div
              key={recipe.id}
              style={{
                ...alchemyStyles.recipeCard,
                ...(craftable ? alchemyStyles.recipeCardCraftable : {}),
              }}
              onClick={() => onSelectRecipe(recipe)}
            >
              <div
                style={{
                  ...alchemyStyles.recipeTier,
                  background: TIER_COLORS[recipe.tier] || '#666',
                  color: recipe.tier === 'legendary' ? '#000' : '#fff',
                }}
              >
                {recipe.tier.toUpperCase()}
              </div>
              <div style={alchemyStyles.recipeName}>{recipe.name}</div>
              <div style={alchemyStyles.recipeStats}>
                {recipe.baseStats.attack && `ATK +${recipe.baseStats.attack}`}
                {recipe.baseStats.attack && recipe.baseStats.hp && ' / '}
                {recipe.baseStats.hp && `HP +${recipe.baseStats.hp}`}
                {recipe.baseStats.defense && ` / DEF +${recipe.baseStats.defense}`}
              </div>
              <div style={alchemyStyles.recipeMaterials}>
                素材: {recipe.materials.length}種類
              </div>
              {craftable && (
                <div style={{ marginTop: '8px', color: alchemyColors.green, fontSize: '11px' }}>
                  ✓ 作成可能
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * 強化タブ
 */
function EnhanceTab({ equipment, onEnhance, playerMaterials }) {
  if (equipment.length === 0) {
    return (
      <div style={alchemyStyles.emptyMessage}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚔️</div>
        <div>装備がありません</div>
        <div style={{ marginTop: '8px', fontSize: '12px' }}>
          「作成」タブで装備を作成しましょう
        </div>
      </div>
    );
  }

  return (
    <div style={alchemyStyles.section}>
      <h2 style={alchemyStyles.sectionTitle}>
        ⚔️ 所持装備 ({equipment.length}個)
      </h2>
      <div style={alchemyStyles.equipmentGrid}>
        {equipment.map((eq) => (
          <div
            key={eq.id}
            style={{
              ...alchemyStyles.equipmentCard,
              borderColor: TIER_COLORS[eq.tier] || '#666',
            }}
          >
            <div style={alchemyStyles.equipmentName}>{eq.name}</div>
            <div style={alchemyStyles.equipmentLevel}>
              Lv.{eq.level} {'★'.repeat(eq.awakening)}
            </div>
            <div style={alchemyStyles.equipmentStats}>
              {eq.stats.attack && `ATK: ${eq.stats.attack}`}
              {eq.stats.hp && ` HP: ${eq.stats.hp}`}
            </div>
            <div style={{ marginTop: '12px' }}>
              <button
                style={{
                  ...alchemyStyles.enhanceButton,
                  ...alchemyStyles.enhanceButtonLevel,
                }}
                onClick={() => onEnhance(eq, 'level')}
              >
                Lv UP
              </button>
              <button
                style={{
                  ...alchemyStyles.enhanceButton,
                  ...alchemyStyles.enhanceButtonAwaken,
                }}
                onClick={() => onEnhance(eq, 'awakening')}
              >
                覚醒
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * 経験値バーコンポーネント
 */
function ExpBar({ currentExp, requiredExp, pendingExp = 0, style = {} }) {
  const currentPercent = Math.min((currentExp / requiredExp) * 100, 100);
  const pendingPercent = Math.min(((currentExp + pendingExp) / requiredExp) * 100, 100);
  const overflowExp = currentExp + pendingExp - requiredExp;
  const hasOverflow = overflowExp > 0;

  return (
    <div style={{ ...style }}>
      <div style={{
        height: '8px',
        background: 'rgba(0, 0, 0, 0.4)',
        borderRadius: '4px',
        overflow: 'hidden',
        position: 'relative',
      }}>
        {/* 予定経験値バー（緑、後ろ） */}
        {pendingExp > 0 && (
          <div style={{
            position: 'absolute',
            left: 0,
            top: 0,
            height: '100%',
            width: `${hasOverflow ? 100 : pendingPercent}%`,
            background: hasOverflow
              ? `repeating-linear-gradient(90deg, ${alchemyColors.green}, ${alchemyColors.gold} 10px, ${alchemyColors.green} 20px)`
              : alchemyColors.green,
            transition: 'width 0.3s ease',
          }} />
        )}
        {/* 現在経験値バー（シアン、前） */}
        <div style={{
          position: 'absolute',
          left: 0,
          top: 0,
          height: '100%',
          width: `${currentPercent}%`,
          background: '#00bcd4',
          transition: 'width 0.3s ease',
        }} />
      </div>
    </div>
  );
}

/**
 * レベルアップ計算ヘルパー関数
 */
function calculateLevelUp(currentLevel, currentExp, expGained) {
  let newLevel = currentLevel;
  let newExp = currentExp + expGained;
  let levelsGained = 0;

  // getRequiredExp を使用（100 * level^1.5）
  while (newExp >= getRequiredExp(newLevel) && newLevel < 100) {
    newExp -= getRequiredExp(newLevel);
    newLevel++;
    levelsGained++;
  }

  return { newLevel, newExp, levelsGained };
}

/**
 * 育成タブ - 経験値オーブを使用してモンスターを育成
 */
function TrainingTab({ playerData, updatePlayerData, allCards }) {
  const [selectedMonster, setSelectedMonster] = useState(null);
  const [selectedOrb, setSelectedOrb] = useState(null);
  const [orbCount, setOrbCount] = useState(1); // 使用するオーブ数
  const [trainingResult, setTrainingResult] = useState(null);

  // 所持モンスター一覧（パーティメンバー優先でソート）
  const ownedMonsters = useMemo(() => {
    if (!playerData?.collection || !allCards) return [];

    const monsterProgress = playerData?.twinKey?.monsterProgress || {};
    const parties = playerData?.twinKey?.parties || [];
    const activePartyId = playerData?.twinKey?.activePartyId;
    const activeParty = parties.find((p) => p.id === activePartyId);
    const activePartyMembers = activeParty?.members || [];
    const activePartyCardIds = new Set(
      activePartyMembers.filter(Boolean).map((m) => m.cardId)
    );

    // コレクションからモンスターカードを抽出
    const monsters = playerData.collection
      .filter((c) => c.quantity > 0)
      .map((collectionCard) => {
        const cardData = allCards.find((card) => card.id === collectionCard.cardId);
        if (!cardData || cardData.type !== 'monster') return null;

        const progress = monsterProgress[collectionCard.cardId] || {
          level: 1,
          exp: 0,
          awakening: 0,
        };
        const isInParty = activePartyCardIds.has(collectionCard.cardId);

        return {
          cardId: collectionCard.cardId,
          rarity: collectionCard.rarity,
          quantity: collectionCard.quantity,
          card: cardData,
          level: progress.level || 1,
          exp: progress.exp || 0,
          awakening: progress.awakening || 0,
          isInParty,
        };
      })
      .filter(Boolean);

    // パーティメンバーを上位に、その後レベル順
    monsters.sort((a, b) => {
      if (a.isInParty && !b.isInParty) return -1;
      if (!a.isInParty && b.isInParty) return 1;
      return b.level - a.level;
    });

    return monsters;
  }, [playerData, allCards]);

  // 所持オーブ取得
  const ownedOrbs = useMemo(() => {
    const materials = playerData?.twinKey?.materials || {};
    const orbs = getAllExpOrbs();
    return orbs
      .map((orb) => ({
        ...orb,
        count: materials[orb.id] || 0,
      }))
      .filter((orb) => orb.count > 0);
  }, [playerData]);

  // 所持ゴールド
  const playerGold = playerData?.twinKey?.gold || 0;

  // 選択中のモンスター情報
  const selectedMonsterInfo = useMemo(() => {
    if (!selectedMonster) return null;
    return selectedMonster;
  }, [selectedMonster]);

  // 最大使用可能なオーブ数
  const maxOrbCount = useMemo(() => {
    if (!selectedOrb || !selectedMonsterInfo) return 0;
    const materials = playerData?.twinKey?.materials || {};
    const availableCount = materials[selectedOrb.id] || 0;

    // ゴールドで制限
    let maxByGold = 0;
    let totalCost = 0;
    let currentLevel = selectedMonsterInfo.level || 1;
    let currentExp = selectedMonsterInfo.exp || 0;

    for (let i = 1; i <= availableCount; i++) {
      // 各オーブ使用時のレベルを考慮したコスト計算
      const { newLevel, newExp } = calculateLevelUp(currentLevel, currentExp, selectedOrb.expValue);
      const cost = calculateOrbUseCost(selectedOrb, currentLevel);
      totalCost += cost;

      if (totalCost <= playerGold) {
        maxByGold = i;
        currentLevel = newLevel;
        currentExp = newExp;
      } else {
        break;
      }
    }

    return Math.min(availableCount, maxByGold, 99); // 最大99個まで
  }, [selectedOrb, selectedMonsterInfo, playerData, playerGold]);

  // オーブ選択時にカウントをリセット
  const handleSelectOrb = useCallback((orb) => {
    if (selectedMonsterInfo) {
      if (selectedOrb?.id === orb.id) {
        setSelectedOrb(null);
        setOrbCount(1);
      } else {
        setSelectedOrb(orb);
        setOrbCount(1);
      }
    }
  }, [selectedMonsterInfo, selectedOrb]);

  // 使用コスト計算（複数オーブ対応）
  const useCostInfo = useMemo(() => {
    if (!selectedOrb || !selectedMonsterInfo || orbCount <= 0) {
      return { totalCost: 0, costPerOrb: [] };
    }

    let totalCost = 0;
    const costPerOrb = [];
    let currentLevel = selectedMonsterInfo.level || 1;
    let currentExp = selectedMonsterInfo.exp || 0;

    for (let i = 0; i < orbCount; i++) {
      const cost = calculateOrbUseCost(selectedOrb, currentLevel);
      costPerOrb.push(cost);
      totalCost += cost;

      const { newLevel, newExp } = calculateLevelUp(currentLevel, currentExp, selectedOrb.expValue);
      currentLevel = newLevel;
      currentExp = newExp;
    }

    return { totalCost, costPerOrb };
  }, [selectedOrb, selectedMonsterInfo, orbCount]);

  // 使用後のプレビュー計算
  const preview = useMemo(() => {
    if (!selectedOrb || !selectedMonsterInfo || orbCount <= 0) return null;

    const totalExpGained = selectedOrb.expValue * orbCount;
    const { newLevel, newExp, levelsGained } = calculateLevelUp(
      selectedMonsterInfo.level || 1,
      selectedMonsterInfo.exp || 0,
      totalExpGained
    );

    const currentRequiredExp = getRequiredExp(selectedMonsterInfo.level || 1);
    const newRequiredExp = getRequiredExp(newLevel);

    return {
      totalExpGained,
      oldLevel: selectedMonsterInfo.level || 1,
      newLevel,
      newExp,
      levelsGained,
      currentRequiredExp,
      newRequiredExp,
    };
  }, [selectedOrb, selectedMonsterInfo, orbCount]);

  // オーブ使用可能かどうか
  const canUseOrb = useMemo(() => {
    if (!selectedOrb || !selectedMonsterInfo || orbCount <= 0) return false;
    if (playerGold < useCostInfo.totalCost) return false;
    const materials = playerData?.twinKey?.materials || {};
    return (materials[selectedOrb.id] || 0) >= orbCount;
  }, [selectedOrb, selectedMonsterInfo, orbCount, playerGold, useCostInfo.totalCost, playerData]);

  // オーブ使用処理
  const handleUseOrb = useCallback(() => {
    if (!canUseOrb || !selectedOrb || !selectedMonsterInfo || !updatePlayerData || !preview) return;

    const cardId = selectedMonsterInfo.cardId;

    updatePlayerData((prev) => {
      const twinKey = { ...(prev.twinKey || {}) };
      const materials = { ...(twinKey.materials || {}) };
      const monsterProgress = { ...(twinKey.monsterProgress || {}) };

      const current = monsterProgress[cardId] || { level: 1, exp: 0, awakening: 0 };
      if (!Number.isSafeInteger(orbCount) || orbCount < 1 || (materials[selectedOrb.id] || 0) < orbCount ||
          (twinKey.gold || 0) < useCostInfo.totalCost || current.level !== preview.oldLevel ||
          current.exp !== (selectedMonsterInfo.exp || 0)) return prev;
      twinKey.gold = (twinKey.gold || 0) - useCostInfo.totalCost;
      // オーブ消費
      materials[selectedOrb.id] = (materials[selectedOrb.id] || 0) - orbCount;
      if (materials[selectedOrb.id] <= 0) {
        delete materials[selectedOrb.id];
      }

      // 経験値・レベル更新
      const progress = monsterProgress[cardId] || { level: 1, exp: 0, awakening: 0 };
      monsterProgress[cardId] = {
        ...progress,
        level: preview.newLevel,
        exp: preview.newExp,
      };

      twinKey.materials = materials;
      twinKey.monsterProgress = monsterProgress;

      return {
        ...prev,
        twinKey,
      };
    });

    // 結果表示
    setTrainingResult({
      success: true,
      monsterName: selectedMonsterInfo.card?.name || selectedMonsterInfo.cardId,
      orbName: selectedOrb.name,
      orbCount,
      expGained: preview.totalExpGained,
      goldSpent: useCostInfo.totalCost,
      oldLevel: preview.oldLevel,
      newLevel: preview.newLevel,
      levelsGained: preview.levelsGained,
      leveledUp: preview.levelsGained > 0,
    });

    // 選択更新（レベル更新を反映）
    setSelectedMonster((prev) =>
      prev
        ? {
            ...prev,
            level: preview.newLevel,
            exp: preview.newExp,
          }
        : null
    );
    setSelectedOrb(null);
    setOrbCount(1);
  }, [canUseOrb, selectedOrb, selectedMonsterInfo, updatePlayerData, useCostInfo.totalCost, preview, orbCount]);

  // 結果ダイアログを閉じる
  const closeResult = useCallback(() => {
    setTrainingResult(null);
  }, []);

  if (ownedMonsters.length === 0) {
    return (
      <div style={alchemyStyles.emptyMessage}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>👥</div>
        <div>モンスターカードを所持していません</div>
        <div style={{ marginTop: '8px', fontSize: '12px' }}>
          パックを開封してモンスターを入手してください
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* 所持ゴールド表示 */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '16px',
          fontSize: '14px',
        }}
      >
        <span style={{ color: '#a0a0b0' }}>探索G: </span>
        <span style={{ color: alchemyColors.gold, fontWeight: 'bold', marginLeft: '8px' }}>
          {playerGold.toLocaleString()} G
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {/* 左側：モンスター選択 */}
        <div>
          <h2 style={alchemyStyles.sectionTitle}>👥 育成対象を選択</h2>
          <div
            style={{
              ...alchemyStyles.cardGrid,
              maxHeight: '500px',
              overflowY: 'auto',
            }}
          >
            {ownedMonsters.map((monster) => {
              const isSelected = selectedMonster?.cardId === monster.cardId;
              const requiredExp = getRequiredExp(monster.level);
              return (
                <div
                  key={monster.cardId}
                  style={{
                    ...alchemyStyles.cardItem,
                    ...(isSelected ? alchemyStyles.cardItemSelected : {}),
                    borderColor: isSelected
                      ? alchemyColors.gold
                      : monster.isInParty
                      ? '#4CAF50'
                      : alchemyColors.purple,
                    position: 'relative',
                    padding: '10px',
                  }}
                  onClick={() => {
                    setSelectedMonster(isSelected ? null : monster);
                    setSelectedOrb(null);
                    setOrbCount(1);
                  }}
                >
                  {monster.isInParty && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '-6px',
                        left: '-6px',
                        background: '#4CAF50',
                        color: '#fff',
                        fontSize: '10px',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontWeight: 'bold',
                      }}
                    >
                      PT
                    </div>
                  )}
                  <div style={alchemyStyles.cardName}>
                    {monster.card?.name || monster.cardId}
                  </div>
                  <div
                    style={{
                      ...alchemyStyles.cardRarity,
                      background: RARITY_COLORS[monster.rarity] || '#666',
                      color: ['SSR', 'GR'].includes(monster.rarity) ? '#000' : '#fff',
                    }}
                  >
                    {monster.rarity}
                  </div>
                  <div style={{ marginTop: '4px', color: alchemyColors.gold, fontSize: '11px' }}>
                    Lv.{monster.level}
                  </div>
                  {/* 経験値表示（現在/必要） */}
                  <div style={{ color: '#a0a0b0', fontSize: '9px', marginTop: '2px' }}>
                    EXP: {monster.exp.toLocaleString()}/{requiredExp.toLocaleString()}
                  </div>
                  {/* 経験値バー */}
                  <ExpBar
                    currentExp={monster.exp}
                    requiredExp={requiredExp}
                    style={{ marginTop: '4px' }}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* 右側：オーブ選択・使用 */}
        <div>
          <h2 style={alchemyStyles.sectionTitle}>🔮 経験値オーブ</h2>
          {ownedOrbs.length === 0 ? (
            <div style={alchemyStyles.emptyMessage}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>🔮</div>
              <div>経験値オーブを所持していません</div>
              <div style={{ marginTop: '8px', fontSize: '11px', color: '#606070' }}>
                ダンジョンで入手できます
              </div>
            </div>
          ) : (
            <div style={alchemyStyles.materialsGrid}>
              {ownedOrbs.map((orb) => {
                const isSelected = selectedOrb?.id === orb.id;
                const cost = selectedMonsterInfo
                  ? calculateOrbUseCost(orb, selectedMonsterInfo.level || 1)
                  : orb.baseCost;
                const canAfford = playerGold >= cost;
                return (
                  <div
                    key={orb.id}
                    style={{
                      ...alchemyStyles.materialCard,
                      ...(isSelected
                        ? { borderColor: alchemyColors.gold, boxShadow: `0 0 12px ${alchemyColors.glowPurple}` }
                        : {}),
                      cursor: selectedMonsterInfo ? 'pointer' : 'not-allowed',
                      opacity: selectedMonsterInfo ? 1 : 0.5,
                    }}
                    onClick={() => handleSelectOrb(orb)}
                  >
                    <div style={alchemyStyles.materialIcon}>{orb.icon}</div>
                    <div style={alchemyStyles.materialName}>{orb.name}</div>
                    <div style={{ color: alchemyColors.green, fontSize: '12px' }}>
                      +{orb.expValue.toLocaleString()} EXP
                    </div>
                    <div style={alchemyStyles.materialCount}>×{orb.count}</div>
                    {selectedMonsterInfo && (
                      <div
                        style={{
                          marginTop: '4px',
                          fontSize: '10px',
                          color: canAfford ? alchemyColors.gold : alchemyColors.red,
                        }}
                      >
                        {cost.toLocaleString()} G
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* 使用プレビュー */}
          {selectedMonsterInfo && selectedOrb && (
            <div
              style={{
                marginTop: '20px',
                padding: '16px',
                borderRadius: '10px',
                background: 'rgba(139, 92, 246, 0.1)',
                border: `1px solid ${alchemyColors.purple}50`,
              }}
            >
              <div style={{ fontSize: '14px', color: '#fff', marginBottom: '12px', textAlign: 'center' }}>
                使用プレビュー
              </div>

              {/* 対象モンスター */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#a0a0b0' }}>対象:</span>
                <span style={{ color: '#fff' }}>{selectedMonsterInfo.card?.name}</span>
              </div>

              {/* オーブ選択 */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ color: '#a0a0b0' }}>オーブ:</span>
                <span style={{ color: '#fff' }}>
                  {selectedOrb.icon} {selectedOrb.name}
                </span>
              </div>

              {/* 使用数量選択 */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '12px',
                padding: '8px',
                background: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '6px',
              }}>
                <span style={{ color: '#a0a0b0' }}>使用数:</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '4px',
                      border: 'none',
                      background: orbCount > 1 ? alchemyColors.purple : '#333',
                      color: orbCount > 1 ? '#fff' : '#666',
                      cursor: orbCount > 1 ? 'pointer' : 'not-allowed',
                      fontSize: '16px',
                      fontWeight: 'bold',
                    }}
                    onClick={() => setOrbCount(Math.max(1, orbCount - 1))}
                    disabled={orbCount <= 1}
                  >
                    -
                  </button>
                  <input
                    type="number"
                    value={orbCount}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 1;
                      setOrbCount(Math.max(1, Math.min(maxOrbCount, val)));
                    }}
                    style={{
                      width: '50px',
                      textAlign: 'center',
                      padding: '4px',
                      borderRadius: '4px',
                      border: `1px solid ${alchemyColors.purple}50`,
                      background: 'rgba(0, 0, 0, 0.3)',
                      color: '#fff',
                      fontSize: '14px',
                    }}
                    min={1}
                    max={maxOrbCount}
                  />
                  <button
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '4px',
                      border: 'none',
                      background: orbCount < maxOrbCount ? alchemyColors.purple : '#333',
                      color: orbCount < maxOrbCount ? '#fff' : '#666',
                      cursor: orbCount < maxOrbCount ? 'pointer' : 'not-allowed',
                      fontSize: '16px',
                      fontWeight: 'bold',
                    }}
                    onClick={() => setOrbCount(Math.min(maxOrbCount, orbCount + 1))}
                    disabled={orbCount >= maxOrbCount}
                  >
                    +
                  </button>
                  <button
                    style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      border: 'none',
                      background: alchemyColors.gold,
                      color: '#000',
                      cursor: 'pointer',
                      fontSize: '11px',
                      fontWeight: 'bold',
                    }}
                    onClick={() => setOrbCount(maxOrbCount)}
                  >
                    MAX
                  </button>
                </div>
              </div>

              {/* 獲得EXP */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#a0a0b0' }}>獲得EXP:</span>
                <span style={{ color: alchemyColors.green, fontWeight: 'bold' }}>
                  +{preview?.totalExpGained.toLocaleString() || 0}
                </span>
              </div>

              {/* 経験値バー（使用前後） */}
              {preview && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '11px', color: '#a0a0b0', marginBottom: '4px' }}>
                    Lv.{preview.oldLevel} EXP: {selectedMonsterInfo.exp.toLocaleString()}/{preview.currentRequiredExp.toLocaleString()}
                  </div>
                  <ExpBar
                    currentExp={selectedMonsterInfo.exp}
                    requiredExp={preview.currentRequiredExp}
                    pendingExp={preview.totalExpGained}
                  />
                  {preview.levelsGained > 0 && (
                    <div style={{
                      marginTop: '8px',
                      fontSize: '11px',
                      color: '#a0a0b0',
                    }}>
                      → Lv.{preview.newLevel} EXP: {preview.newExp.toLocaleString()}/{preview.newRequiredExp.toLocaleString()}
                    </div>
                  )}
                </div>
              )}

              {/* レベル予測 */}
              {preview && preview.levelsGained > 0 && (
                <div style={{
                  padding: '8px',
                  background: 'rgba(255, 215, 0, 0.1)',
                  borderRadius: '6px',
                  marginBottom: '12px',
                  textAlign: 'center',
                }}>
                  <span style={{ color: alchemyColors.gold, fontSize: '14px', fontWeight: 'bold' }}>
                    🎉 Lv.{preview.oldLevel} → Lv.{preview.newLevel}
                  </span>
                  <span style={{ color: '#a0a0b0', fontSize: '11px', marginLeft: '8px' }}>
                    (+{preview.levelsGained}レベル)
                  </span>
                </div>
              )}

              {/* 必要ゴールド */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '12px',
                  paddingTop: '8px',
                  borderTop: `1px solid ${alchemyColors.purple}30`,
                }}
              >
                <span style={{ color: '#a0a0b0' }}>必要な探索G:</span>
                <span
                  style={{
                    color: playerGold >= useCostInfo.totalCost ? alchemyColors.gold : alchemyColors.red,
                    fontWeight: 'bold',
                  }}
                >
                  {useCostInfo.totalCost.toLocaleString()} G
                </span>
              </div>

              <button
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: 'none',
                  background: canUseOrb
                    ? `linear-gradient(135deg, ${alchemyColors.green}, ${alchemyColors.blue})`
                    : '#333',
                  color: canUseOrb ? '#fff' : '#666',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: canUseOrb ? 'pointer' : 'not-allowed',
                }}
                onClick={handleUseOrb}
                disabled={!canUseOrb}
              >
                {canUseOrb
                  ? `📈 使用する（×${orbCount}）`
                  : playerGold < useCostInfo.totalCost
                    ? '探索G不足'
                    : '使用不可'}
              </button>
            </div>
          )}

          {/* モンスター未選択時のガイド */}
          {!selectedMonsterInfo && ownedOrbs.length > 0 && (
            <div
              style={{
                marginTop: '20px',
                padding: '16px',
                textAlign: 'center',
                color: '#606070',
                fontSize: '13px',
              }}
            >
              ← 左からモンスターを選択してください
            </div>
          )}
        </div>
      </div>

      {/* 育成結果ダイアログ */}
      {trainingResult && (
        <div style={alchemyStyles.modal} onClick={closeResult}>
          <div
            style={{
              ...alchemyStyles.modalContent,
              textAlign: 'center',
              border: `2px solid ${trainingResult.leveledUp ? alchemyColors.gold : alchemyColors.green}`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>
              {trainingResult.leveledUp ? '🎉' : '📈'}
            </div>
            <h2
              style={{
                ...alchemyStyles.modalTitle,
                marginBottom: '8px',
              }}
            >
              {trainingResult.leveledUp ? 'レベルアップ！' : '育成完了'}
            </h2>

            <div style={{ color: '#fff', fontSize: '16px', marginBottom: '16px' }}>
              {trainingResult.monsterName}
            </div>

            <div
              style={{
                background: 'rgba(139, 92, 246, 0.1)',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '20px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#a0a0b0' }}>使用オーブ:</span>
                <span style={{ color: '#fff' }}>{trainingResult.orbName} ×{trainingResult.orbCount}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#a0a0b0' }}>獲得EXP:</span>
                <span style={{ color: alchemyColors.green }}>
                  +{trainingResult.expGained.toLocaleString()}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#a0a0b0' }}>消費した探索G:</span>
                <span style={{ color: alchemyColors.gold }}>
                  -{trainingResult.goldSpent.toLocaleString()} G
                </span>
              </div>
              {trainingResult.leveledUp && (
                <div
                  style={{
                    marginTop: '12px',
                    paddingTop: '12px',
                    borderTop: `1px solid ${alchemyColors.purple}30`,
                    fontSize: '18px',
                    fontWeight: 'bold',
                  }}
                >
                  <span style={{ color: '#a0a0b0' }}>Lv.{trainingResult.oldLevel}</span>
                  <span style={{ color: alchemyColors.gold, margin: '0 12px' }}>→</span>
                  <span style={{ color: alchemyColors.gold }}>Lv.{trainingResult.newLevel}</span>
                  {trainingResult.levelsGained > 1 && (
                    <span style={{ color: '#a0a0b0', fontSize: '12px', marginLeft: '8px' }}>
                      (+{trainingResult.levelsGained})
                    </span>
                  )}
                </div>
              )}
            </div>

            <button
              style={{
                ...alchemyStyles.enhanceButton,
                padding: '12px 48px',
                fontSize: '14px',
                background: `linear-gradient(135deg, ${alchemyColors.purple}, ${alchemyColors.gold})`,
                color: '#fff',
              }}
              onClick={closeResult}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * レシピ詳細モーダル
 */
function RecipeModal({ recipe, canCraft, playerMaterials, onCraft, onClose }) {
  return (
    <div style={alchemyStyles.modal} onClick={onClose}>
      <div
        style={alchemyStyles.modalContent}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={alchemyStyles.modalTitle}>
          {recipe.name}
        </h2>

        <div style={{ marginBottom: '16px' }}>
          <div
            style={{
              ...alchemyStyles.recipeTier,
              background: TIER_COLORS[recipe.tier] || '#666',
              color: recipe.tier === 'legendary' ? '#000' : '#fff',
            }}
          >
            {recipe.tier.toUpperCase()}
          </div>
          <span style={{ marginLeft: '8px', color: '#a0a0b0' }}>
            {recipe.type}
          </span>
        </div>

        {recipe.description && (
          <p style={{ color: '#a0a0b0', fontSize: '13px', marginBottom: '16px' }}>
            {recipe.description}
          </p>
        )}

        {/* ステータス */}
        <div style={{ marginBottom: '16px' }}>
          <h3 style={{ color: alchemyColors.gold, fontSize: '14px', marginBottom: '8px' }}>
            基本ステータス
          </h3>
          <div style={{ color: '#fff', fontSize: '13px' }}>
            {recipe.baseStats.attack && <div>ATK +{recipe.baseStats.attack}</div>}
            {recipe.baseStats.hp && <div>HP +{recipe.baseStats.hp}</div>}
            {recipe.baseStats.defense && <div>DEF +{recipe.baseStats.defense}</div>}
            {recipe.baseStats.speed && <div>SPD +{recipe.baseStats.speed}</div>}
          </div>
        </div>

        {/* 効果 */}
        {recipe.effects && recipe.effects.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <h3 style={{ color: alchemyColors.gold, fontSize: '14px', marginBottom: '8px' }}>
              効果
            </h3>
            {recipe.effects.map((effect, idx) => (
              <div key={idx} style={{ color: '#a0a0b0', fontSize: '12px' }}>
                • {getEffectDescription(effect)}
              </div>
            ))}
          </div>
        )}

        {/* 必要素材 */}
        <div style={{ marginBottom: '16px' }}>
          <h3 style={{ color: alchemyColors.gold, fontSize: '14px', marginBottom: '8px' }}>
            必要素材
          </h3>
          {recipe.materials.map((mat) => {
            const have = playerMaterials[mat.type] || 0;
            const sufficient = have >= mat.count;
            const matInfo = getMaterialInfo(mat.type);

            return (
              <div key={mat.type} style={alchemyStyles.materialRequirement}>
                <span>
                  {matInfo?.icon || '📦'} {matInfo?.name || mat.type}
                </span>
                <span
                  style={
                    sufficient
                      ? alchemyStyles.materialSufficient
                      : alchemyStyles.materialInsufficient
                  }
                >
                  {have} / {mat.count}
                </span>
              </div>
            );
          })}
        </div>

        <div style={alchemyStyles.modalActions}>
          <button
            style={{
              ...alchemyStyles.enhanceButton,
              padding: '12px 32px',
              fontSize: '14px',
              background: canCraft
                ? `linear-gradient(135deg, ${alchemyColors.purple}, ${alchemyColors.gold})`
                : '#333',
              color: canCraft ? '#fff' : '#666',
              cursor: canCraft ? 'pointer' : 'not-allowed',
            }}
            onClick={canCraft ? onCraft : undefined}
            disabled={!canCraft}
          >
            {canCraft ? '🔨 作成する' : '素材不足'}
          </button>
          <button
            style={{
              ...alchemyStyles.enhanceButton,
              padding: '12px 32px',
              fontSize: '14px',
              background: 'transparent',
              border: `1px solid ${alchemyColors.purple}`,
              color: '#fff',
            }}
            onClick={onClose}
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * 結果表示モーダル（分解・作成共通）
 */
function ResultModal({ type, result, onClose }) {
  const isDecompose = type === 'decompose';
  const isCraft = type === 'craft';

  // 分解成功時の表示
  if (isDecompose && result.success) {
    return (
      <div style={alchemyStyles.modal} onClick={onClose}>
        <div
          style={{
            ...alchemyStyles.modalContent,
            textAlign: 'center',
            border: `2px solid ${alchemyColors.gold}`,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* 分解成功アイコン */}
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>🔥</div>
          <h2 style={{
            ...alchemyStyles.modalTitle,
            marginBottom: '8px',
          }}>
            分解完了
          </h2>
          <div style={{ color: '#a0a0b0', marginBottom: '20px' }}>
            <span style={{ color: '#fff' }}>{result.cardName}</span>
            <span
              style={{
                marginLeft: '8px',
                padding: '2px 8px',
                borderRadius: '4px',
                background: RARITY_COLORS[result.rarity] || '#666',
                color: ['SSR', 'GR'].includes(result.rarity) ? '#000' : '#fff',
                fontSize: '12px',
              }}
            >
              {result.rarity}
            </span>
          </div>

          {/* 獲得素材リスト */}
          <div style={{
            background: 'rgba(139, 92, 246, 0.1)',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px',
          }}>
            <div style={{ color: alchemyColors.gold, fontSize: '14px', marginBottom: '12px' }}>
              ✨ 獲得素材
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '8px',
            }}>
              {result.materials.map((mat, idx) => {
                const info = getMaterialInfo(mat.type);
                return (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 12px',
                      background: 'rgba(26, 26, 46, 0.8)',
                      borderRadius: '6px',
                      border: `1px solid ${TIER_COLORS[info?.tier] || '#666'}40`,
                    }}
                  >
                    <span style={{ fontSize: '12px', color: '#fff' }}>
                      {info?.icon || '📦'} {info?.name || mat.type}
                    </span>
                    <span style={{
                      color: alchemyColors.green,
                      fontWeight: 'bold',
                      fontSize: '14px',
                    }}>
                      +{mat.count}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <button
            style={{
              ...alchemyStyles.enhanceButton,
              padding: '12px 48px',
              fontSize: '14px',
              background: `linear-gradient(135deg, ${alchemyColors.purple}, ${alchemyColors.gold})`,
              color: '#fff',
            }}
            onClick={onClose}
          >
            OK
          </button>
        </div>
      </div>
    );
  }

  // 作成成功時の表示
  if (isCraft && result.success) {
    return (
      <div style={alchemyStyles.modal} onClick={onClose}>
        <div
          style={{
            ...alchemyStyles.modalContent,
            textAlign: 'center',
            border: `2px solid ${alchemyColors.gold}`,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* 作成成功アイコン */}
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>⚔️</div>
          <h2 style={{
            ...alchemyStyles.modalTitle,
            marginBottom: '8px',
          }}>
            作成完了
          </h2>
          <div style={{
            padding: '16px',
            background: 'rgba(139, 92, 246, 0.1)',
            borderRadius: '8px',
            marginBottom: '20px',
          }}>
            <div style={{
              color: '#fff',
              fontSize: '18px',
              fontWeight: 'bold',
              marginBottom: '8px',
            }}>
              {result.equipmentName}
            </div>
            <div
              style={{
                ...alchemyStyles.recipeTier,
                background: TIER_COLORS[result.tier] || '#666',
                color: result.tier === 'legendary' ? '#000' : '#fff',
                marginBottom: '12px',
              }}
            >
              {result.tier?.toUpperCase() || 'COMMON'}
            </div>
            <div style={{ color: '#a0a0b0', fontSize: '13px' }}>
              {result.stats?.attack && <span>ATK +{result.stats.attack}</span>}
              {result.stats?.attack && result.stats?.hp && <span> / </span>}
              {result.stats?.hp && <span>HP +{result.stats.hp}</span>}
              {result.stats?.defense && <span> / DEF +{result.stats.defense}</span>}
            </div>
          </div>

          <button
            style={{
              ...alchemyStyles.enhanceButton,
              padding: '12px 48px',
              fontSize: '14px',
              background: `linear-gradient(135deg, ${alchemyColors.purple}, ${alchemyColors.gold})`,
              color: '#fff',
            }}
            onClick={onClose}
          >
            OK
          </button>
        </div>
      </div>
    );
  }

  // エラー時の表示（分解・作成共通）
  return (
    <div style={alchemyStyles.modal} onClick={onClose}>
      <div
        style={{
          ...alchemyStyles.modalContent,
          textAlign: 'center',
          border: `2px solid ${alchemyColors.red}`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
        <h2 style={{
          ...alchemyStyles.modalTitle,
          color: alchemyColors.red,
          marginBottom: '16px',
        }}>
          {result.message || 'エラーが発生しました'}
        </h2>

        {/* 不足素材の表示（作成失敗時） */}
        {result.missingMaterials && result.missingMaterials.length > 0 && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px',
          }}>
            <div style={{ color: alchemyColors.red, fontSize: '13px', marginBottom: '12px' }}>
              不足している素材:
            </div>
            {result.missingMaterials.map((mat, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '6px 0',
                  borderBottom: idx < result.missingMaterials.length - 1
                    ? '1px solid rgba(239, 68, 68, 0.2)'
                    : 'none',
                  fontSize: '12px',
                }}
              >
                <span style={{ color: '#fff' }}>{mat.name}</span>
                <span style={{ color: alchemyColors.red }}>
                  {mat.have} / {mat.need}
                </span>
              </div>
            ))}
          </div>
        )}

        <button
          style={{
            ...alchemyStyles.enhanceButton,
            padding: '12px 48px',
            fontSize: '14px',
            background: 'transparent',
            border: `1px solid ${alchemyColors.purple}`,
            color: '#fff',
          }}
          onClick={onClose}
        >
          閉じる
        </button>
      </div>
    </div>
  );
}

export default AlchemyScreen;
